import { has as xHas } from 'lodash';

function OdPositive(n, tol = 1.e-10)
{
    return (n > tol);
}

function OdNegative(n, tol = 1.e-10)
{
    return (n < -tol);
}

function OdZero(n, tol = 1.e-10)
{
    return !OdPositive(n, tol) && !OdNegative(n, tol);
}

function OdNonZero(n, tol = 1.e-10)
{
    return OdPositive(n, tol) || OdNegative(n, tol);
}

function OdEqual(n, m, tol = 1.e-10)
{
    return OdZero(n - m, tol);
}

function OdLess(n, m, tol = 1.e-10)
{
    return n < m - tol;
}

function OdLessOrEqual(n, m, tol = 1.e-10)
{
    return n <= m + tol;
}

function OdGreater(n, m, tol = 1.e-10)
{
    return n > m + tol;
}

function OdGreaterOrEqual(n, m, tol = 1.e-10)
{
    return n >= m - tol;
}

export class Point2D {
    constructor(x, y) {
        this._x = x || 0;
        this._y = y || 0;
    }

    get x() { return this._x; }
    set x(value) { this._x = value; }

    get y() { return this._y; }
    set y(value) { this._y = value; }

    static make(from) {
        return new Point2D(from.x, from.y);
    }

    sub(other) {
        return new Vector2D(this.x - other.x, this.y - other.y);
    }

    addVector(v) {
        this.x += v.x;
        this.y += v.y;
        return this;
    }
}


export class Vector2D {
    constructor(x, y) {
        this._x = x || 0;
        this._y = y || 0;
    }

    get x() { return this._x; }
    set x(value) { this._x = value; }

    get y() { return this._y; }
    set y(value) { this._y = value; }

    get lengthSqrd() { return this._x*this._x + this._y*this._y; }

    get length() { return Math.sqrt(this.lengthSqrd); }

    set(x, y) {
        this._x = x;
        this._y = y;
        return this;
    }

    normalize() {
        const l = Math.sqrt(this.lengthSqrd);
        this._x /= l;
        this._y /= l;
        return this;
    }

    normalized() {
        const v = new Vector2D(this._x, this._y);
        return v.normalize();
    }

    getMultiplyScalar(d) {
        return new Vector2D(this._x, this._y).multiplyScalar(d);
    }

    multiplyScalar(d) {
        this._x *= d;
        this._y *= d;
        return this;
    }

    dotProduct(other)
    {
        return this._x * other._x + this._y * other._y;
    }

    rotateBy(angle)
    {
        const s = Math.sin(angle);
        const c = Math.cos(angle);

        return this.set(this._x * c - this._y * s, this._x * s + this._y * c);
    }

    rotated(angle)
    {
        const r = new Vector2D(this._x, this._y);
        return r.rotateBy(angle);
    }

    angle()
    {
        let ret = (OdZero(this._x) && OdZero(this._y)) ? 0.0 : Math.atan2(this._y, this._x);
        if (ret < 0)
        {
            ret += Math.PI+Math.PI;
        }
        else
        {
            if (OdZero(ret))
            {
                if (OdZero(this._x))
                {
                    if (OdLessOrEqual(this._y, 0.0))
                        ret = 1.5 * Math.PI;
                    else
                        ret = 0.5 * Math.PI;
                }
                else if (this._x < 0.0)
                    ret = Math.PI;
            }
        }
        return ret;
    }

    angleTo(other)
    {
        const t = this.normalized();
        const o = other.normalized();
        const d = t.dotProduct(o);
        if (d >= 1)
            return 0;
        if (d <= -1)
            return Math.PI;
        return Math.acos(d);
    }
}


export class LineSegment2D {
    constructor(p, v) {
        if (p instanceof Point2D &&
            v instanceof Vector2D)
        {
            this._point = p;
            this._vector = v;
        }
        else {
            this._point = new Point2D();
            this._vector = new Vector2D();
        }
    }

    set point(value) { this._point = value; }
    get point() { return this._point; }

    set vector(value) { this._vector = value; }
    get vector() { return this._vector; }
}


export class Ray2D {
    constructor(p, v) {
        if (p instanceof Point2D &&
            v instanceof Vector2D) {
            this._point = p;
            this._vector = v;
        } else {
            this._point = new Point2D();
            this._vector = new Vector2D();
        }
    }

    set point(value) {
        this._point = value;
    }

    get point() {
        return this._point;
    }

    set vector(value) {
        this._vector = value;
    }

    get vector() {
        return this._vector;
    }

    intersectWith(line) {
        const v = this.vector, lv = line.vector;
        const d = lv.x * v.y - v.x * lv.y;
        if (Math.abs(d) > v.lengthSqrd * 1e-10) {
            const d1 = lv.x * (line.point.y - this.point.y) - (line.point.x - this.point.x) * lv.y;
            const t1 = d1 / d;
            if (t1 < 0) return false;

            const intPt = new Point2D(this.point.x + v.x * t1, this.point.y + v.y * t1);

            const vl = new Vector2D(intPt.x - line.point.x, intPt.y - line.point.y);
            const param = (lv.x * vl.x + lv.y * vl.y) / lv.lengthSqrd;

            if (param >= 0 && param <= 1)
                return intPt;
        }
        return false;
    }
}
